import type { CartesianAxisOption } from 'echarts/types/src/coord/cartesian/AxisModel';
import { type EChartsExtensionInstallRegisters } from 'echarts/types/src/extension';
import type { ECUnitOption } from 'echarts/types/src/util/types';

/**
 * 坐标轴标签对齐扩展组件
 * 
 * 主要功能：
 * 1. 扩展标签对齐配置：在坐标轴标签配置中添加 dvAlignEdge 选项，控制标签是否边缘对齐
 * 2. 支持多种轴类型：支持 xAxis、yAxis 和 singleAxis（单轴，主要用于词流图）
 * 3. 智能对齐逻辑：
 *    - 当 dvAlignEdge: true 时：水平轴左右对齐，垂直轴上下对齐
 *    - 当 dvAlignEdge: false 时：默认居中对齐
 * 4. 预处理器模式：通过 registerPreprocessor 在图表渲染前处理轴配置
 * 
 * 这个组件解决了 ECharts 默认标签对齐的局限性，提供了更灵活的标签布局控制
 */

// 已经适用的轴线类型
// 【需要注意】：目前单轴只在水平方向上有应用：词流图，垂直方向上可能有未知问题
const usageAxisTypes = ['xAxis', 'yAxis', 'singleAxis'];

function getAlignConfig(dvAlignEdge: boolean, isHorizontal: boolean) {
  if (!dvAlignEdge) {
    return isHorizontal
      ? { alignMinLabel: 'center', alignMaxLabel: 'center' }
      : { verticalAlignMinLabel: 'middle', verticalAlignMaxLabel: 'middle' };
  }

  return isHorizontal
    ? { alignMinLabel: 'left', alignMaxLabel: 'right' }
    : { verticalAlignMinLabel: 'bottom', verticalAlignMaxLabel: 'top' };
}

function processAxis(
  axis: CartesianAxisOption & {
    axisLabel: CartesianAxisOption['axisLabel'] & { dvAlignEdge: boolean };
  },
  axisType: string
) {
  if (!axis || !axis.axisLabel) {
    return;
  }

  const { dvAlignEdge } = axis.axisLabel;
  if (dvAlignEdge == null) {
    return;
  }

  const isHorizontal = axisType === 'xAxis' || axisType === 'singleAxis';
  const alignConfig = getAlignConfig(dvAlignEdge, isHorizontal);

  Object.assign(axis.axisLabel, alignConfig);
}

function installDvAlignEdge(registers: EChartsExtensionInstallRegisters) {
  registers.registerPreprocessor((option: ECUnitOption) => {
    usageAxisTypes.forEach(axisType => {
      const axisConfig = option[axisType];
      if (!axisConfig) {
        return;
      }

      const axes = Array.isArray(axisConfig) ? axisConfig : [axisConfig];
      axes.forEach(axis => processAxis(axis, axisType));
    });
  });
}
export function install(registers: EChartsExtensionInstallRegisters) {
  installDvAlignEdge(registers);
}
